import React, { useState, useEffect, useCallback, useMemo } from "react";

// components
import MDBox from "components/MDBox";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import Header from "./Header/Header";
import Step1 from "./Step1/Step1";
import Step2 from "./Step2/Step2";
import Step3 from "./Step3/Step3";

// services
import useModels from "hooks/useModels.hook";
import useTestParams from "hooks/useTestParams.hook";
import { styles } from "./MainPage.styles";
import { Grid } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import classNames from "classnames";

// sounds
import TermsAndConditions from "./TermsAndConditions/TermsAndConditions";
import InstractionsScreen from "./InstractionsScreen/InstractionsScreen";
import TestResults from "./TestResults/TestResults";
import SelfReportScreen from "./SelfReportScreen/SelfReportScreen";
import ModalsActions from "./ModalsActions/ModalsActions";

import { useAppSelector } from "store/hooks";

import { ResultActionType, UserActions } from "types/resultActions";
import { checkCameraPermission } from "utils/camera.util";
import { TestType } from "types/testType";
import { sentryLogException, sentryLogMessage } from "services/sentryLogger";
import { detectHmo } from "utils/hmoDetection";
import { HMO } from "constants/hmo";
import type {
  StepComponentProps,
  TestTypeStepMap,
  TermsAndConditionsProps,
  InstractionsScreenProps,
  Step1Props,
  Step2Props,
  Step3Props,
  TestResultsProps,
  SelfReportScreenProps,
  ModalsActionsProps,
  StepPropsContext,
  PropsFactory,
} from "./MainPage.types";
import { getModelMetaDataTags } from "services/models";
import { SENTRY_EVENT_TYPES } from "constants/sentryEvents";

const DEFAULT_TEST_TYPE = TestType.FOB;

const MainPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [resultAction, setResultAction] = useState<ResultActionType>();
  const useStyles = makeStyles()((theme) => styles(theme));
  const { classes } = useStyles();
  const result = useAppSelector((state) => state.imageRecognition.result);
  
  // Use custom hook for test params (handles URL parsing and Redux sync)
  const { testType, shouldMoveToCamera } = useTestParams();
  useModels();
  
  // Detect HMO to determine if we should show self-report screen
  const isClinicalTrialHmo = detectHmo() === HMO.CLINICAL_TRIAL;

  useEffect(() => {
    (async () => {
      try {
        await checkCameraPermission();
        if (shouldMoveToCamera()) {
          setCurrentStep(4);
        }
      } catch (error) {
        sentryLogException({
          error,
          tags: {
            component: 'MainPage',
            category: 'cameraPermission',
            ...getModelMetaDataTags()
          },
          extra: {
            testType,
            timestamp: new Date().toISOString(),
          },
        });
      }
    })();
  }, [shouldMoveToCamera]);

  useEffect(() => {
    if (result === "INVALID" || result === "REJECTED") {
      setResultAction("INVALID_TEST");
    }
  }, [result]);

  const handleUserAction = useCallback((userAction: UserActions) => {
    const sentryMessageMap: Partial<Record<UserActions, string>> = {
      WRONG_RESULT: "Wrong Result Reported by the user",
      SELF_REPORTING_ISSUE: "Issue reported by the user",
    }

    const message = sentryMessageMap[userAction]
    if (message) {
      try {
        sentryLogMessage({
          message,
          level: "warning",
          tags: {
            component: "Test Completed With Issue",
            category: "test_completed_with_issue",
            event_type: SENTRY_EVENT_TYPES.COMPLETED_TEST_WITH_ISSUE,
            user_action: userAction,
            ...getModelMetaDataTags(),
          },
          extra: {
            timestamp: new Date().toISOString(),
          },
        })
      } catch (error) {
        console.error("MainPage: Test Completed With Issue - Sentry logging or flush failed:", error)
      }
    }
    setResultAction('DONE')
  }, []);

  const handleUpdateStepLocal = useCallback((step: number) => {
    setCurrentStep((prev) => {
      // Validate step is within bounds (0-6)
      if (step < 0 || step > 6) {
        return prev;
      }
      return step;
    });
  }, []);

  // Separate stepMap from result-dependent props to prevent unnecessary remounts
  // Result is only used in step 5's activeInnerStep prop
  const stepMap: TestTypeStepMap = useMemo(() => {
    return {
    [TestType.FOB]: {
      0: {
        Component: TermsAndConditions as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as TermsAndConditionsProps,
        contentHeight: "calc(100dvh - 285px)",
      },
      1: {
        Component: InstractionsScreen as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as InstractionsScreenProps,
      },
      2: {
        Component: Step1 as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as Step1Props,
      },
      3: {
        Component: Step2 as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as Step2Props,
      },
      4: {
        Component: Step3 as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as Step3Props,
      },
      5: {
        Component: (isClinicalTrialHmo ? SelfReportScreen : TestResults) as React.ComponentType<StepComponentProps>,
        // Use props factory for dynamic result-dependent props (prevents remounting other steps)
        props: isClinicalTrialHmo
          ? {
              handleUpdateStep: handleUpdateStepLocal,
              setResultAction: setResultAction,
            } as SelfReportScreenProps
          : ((context: StepPropsContext): TestResultsProps => ({
              handleUpdateStep: handleUpdateStepLocal,
              activeInnerStep: context.result === "POSITIVE" ? 1 : (context.result === "INVALID" ? 3 : 2),
              setResultAction: setResultAction,
            })) as PropsFactory<TestResultsProps>,
        contentHeight: isClinicalTrialHmo ? "calc(100dvh - 130px)" : undefined,
      },
      6: {
        Component: ModalsActions as React.ComponentType<StepComponentProps>,
        // Use props factory for dynamic resultAction-dependent props
        props: ((context: StepPropsContext): ModalsActionsProps => ({
          handleUserAction: handleUserAction,
          resultAction: context.resultAction,
          handleUpdateStep: handleUpdateStepLocal,
          setResultAction: setResultAction,
        })) as PropsFactory<ModalsActionsProps>,
      },
    },
    [TestType.HPYLORI]: {
      0: {
        Component: TermsAndConditions as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as TermsAndConditionsProps,
        contentHeight: "calc(100dvh - 285px)",
      },
      1: {
        Component: InstractionsScreen as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as InstractionsScreenProps,
      },
      2: {
        Component: Step1 as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as Step1Props,
      },
      3: {
        Component: Step2 as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as Step2Props,
      },
      4: {
        Component: Step3 as React.ComponentType<StepComponentProps>,
        props: { handleUpdateStep: handleUpdateStepLocal } as Step3Props,
      },
      5: {
        Component: (isClinicalTrialHmo ? SelfReportScreen : TestResults) as React.ComponentType<StepComponentProps>,
        // Use props factory for dynamic result-dependent props (prevents remounting other steps)
        props: isClinicalTrialHmo
          ? {
              handleUpdateStep: handleUpdateStepLocal,
              setResultAction: setResultAction,
            } as SelfReportScreenProps
          : ((context: StepPropsContext): TestResultsProps => ({
              handleUpdateStep: handleUpdateStepLocal,
              activeInnerStep: context.result === "POSITIVE" ? 1 : (context.result === "INVALID" ? 3 : 2),
              setResultAction: setResultAction,
            })) as PropsFactory<TestResultsProps>,
        contentHeight: isClinicalTrialHmo ? "calc(100dvh - 130px)" : undefined,
      },
      6: {
        Component: ModalsActions as React.ComponentType<StepComponentProps>,
        // Use props factory for dynamic resultAction-dependent props
        props: ((context: StepPropsContext): ModalsActionsProps => ({
          handleUserAction: handleUserAction,
          resultAction: context.resultAction,
          handleUpdateStep: handleUpdateStepLocal,
          setResultAction: setResultAction,
        })) as PropsFactory<ModalsActionsProps>,
      },
    },
  };
  // IMPORTANT: Do NOT include 'result' in dependencies - it causes Step3 to remount when result changes
  // Dynamic props are handled via PropsFactory functions instead
  }, [handleUserAction, handleUpdateStepLocal, isClinicalTrialHmo, setResultAction]);

  const currentStepMap = useMemo(() => {
    return stepMap[testType] || stepMap[DEFAULT_TEST_TYPE];
  }, [stepMap, testType]);

  const currentStepConfig = useMemo(() => {
    return currentStepMap[currentStep];
  }, [currentStepMap, currentStep]);

  // Use the component directly - don't create inline wrapper which causes remounts
  const StepComponent = currentStepConfig?.Component;
  
  // Context for props factory functions
  const propsContext: StepPropsContext = useMemo(() => ({
    result,
    resultAction,
  }), [result, resultAction]);

  const stepProps = useMemo(() => {
    const props = currentStepConfig?.props;
    
    if (!props) {
      return {} as StepComponentProps;
    }
    
    // If props is a factory function, call it with current context
    if (typeof props === 'function') {
      return props(propsContext) as StepComponentProps;
    }
    
    // Otherwise, use static props directly
    return props as StepComponentProps;
  }, [currentStepConfig, propsContext]);

  return (
    <DashboardLayout>
      <Grid className="mainpage-grid" container mt={0} spacing={0}>
        <MDBox pb={1.5}>
          <Header currentStep={currentStep} />
          <div
            className={classNames(
              classes.bodyContainer,
              currentStep === 6 ? classes.step_MODALS : ""
            )}
            style={{ 
              height: currentStepConfig?.contentHeight 
                ? `calc(${currentStepConfig.contentHeight.replace(/^calc\(|\)$/g, '')} - 50px)` 
                : undefined 
            }}
          >
            {StepComponent && (
              <StepComponent 
                key={`${testType}-${currentStep}`} 
                {...stepProps} 
              />
            )}
          </div>
        </MDBox>
      </Grid>
    </DashboardLayout>
  );
};
export default MainPage;
